import { Component, createRef } from 'react';
import EditorFaq from "../components/EditorFaq";
import GameData from "../mobx/GameData";
import { NamEditor } from "../components/nes/NamEditor";
import { observe } from "mobx";
import { Accordion, Container, Row, Col, Form } from "react-bootstrap";
import { coerceUnknownFields } from '../data/BaseGame';
import ApplicationState from '../mobx/ApplicationState';

function cleanTitle() {
    let title = coerceUnknownFields(GameData.currentGame).title;
    while (title.length < 28) {
        title += ' ';
    }
    return title;
}

function getGoalTile() {
    const td = coerceUnknownFields(GameData.currentGame).tileData,
        tileId = td.findIndex(a => a.collision === 7) ?? td.findIndex(a => a.collision === 4) ?? td.findIndex(a => a.collision === 5) ?? 0,
        biggerTileId = (tileId > 7 ? 32 : 0) + ((tileId & 0x07) << 1);

    return {
        tile: [
            [biggerTileId, biggerTileId + 1],
            [biggerTileId + 16, biggerTileId + 17]
        ],
        palette: td[tileId].palette,
        text: td[tileId].collision === 7 ? " Exit" : "01/04"
    };

}

export default class HudPage extends Component {

    gameDataCleanup = null;
    protectedRegion = null;
    namEditor = createRef();
    possibleProtectedRegions = []

    constructor(props) {
        super(props);
        this.state = {
            protectedRegions: [],
            gameSettings: {
                showLevelEnabled: null,
                showKeyCount: null,
                showGameTitle: null,
                showGoal: null
            }
        };

        this.possibleProtectedRegions = [
            {test: () => true, region: {x1: 0, x2: 32, y1: 6, y2: 30}, data: null},
            {test: g => g.showLevelEnabled, region: {x1: 2, y1: 4, x2: 13, y2: 4}, data: "Level: 01/99"},
            {test: g => g.showKeyCount, region: {x1: 2, y1: 3, x2: 4, y2: 3}, data: [0xf8, 0x90, 0x91]},
            {test: g => g.showGameTitle, region: {x1: 2, y1: 1, x2: 0x1d, y2: 1}, data: cleanTitle()},
            // Goal image
            {test: g => g.showGoal, region: {x1: 22, y1: 2, x2: 23, y2: 2}, palette: getGoalTile().palette, data: getGoalTile().tile[0]},
            {test: g => g.showGoal, region: {x1: 22, y1: 3, x2: 23, y2: 3}, palette: getGoalTile().palette, data: getGoalTile().tile[1]},
            // Goal text
            {test: g => g.showGoal, region: {x1: 20, y1: 4, x2: 24, y2: 4}, data: getGoalTile().text},
        ]

        const game = coerceUnknownFields(GameData.currentGame);
        Object.keys(this.state.gameSettings).forEach(setting => {
            this.state.gameSettings[setting] = game[setting];
        });
    }

    async componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });
        await this.buildProtectedRegions();
        await this.rewriteProtectedAreas();
        // Rewriting via NamEditor set this flag, but the user hasn't done anything yet. 
        ApplicationState.unsetCurrentPageUnsaved(); 
        this.forceUpdate();
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
    }

    async buildProtectedRegions() {
        return new Promise(resolve => this.setState({protectedRegions: this.possibleProtectedRegions.filter(a => a.test(this.state.gameSettings)).map(a => a.region) }, resolve))
    }

    async rewriteProtectedAreas() {
        for (let reg = 0; reg < this.possibleProtectedRegions.length; reg++) {
            const region = this.possibleProtectedRegions[reg];
            if (region.test(this.state.gameSettings)) {
                if (typeof region.data === 'string') {
                    for (let i = 0; i < region.data.length; i++) {
                        await this.namEditor.current.setTile(region.region.x1+i, region.region.y1, region.data.charCodeAt(i) + 0x60, region.palette);
                    }
                } else if (Array.isArray(region.data)) {
                    for (let i = 0; i < region.data.length; i++) {
                        await this.namEditor.current.setTile(region.region.x1+i, region.region.y1, region.data[i], region.palette);
                    }
                }
            }
        }
    }

    async doSave() {
        GameData.replaceCurrentGame({
            ...GameData.currentGame, 
            ...this.state.gameSettings
        });
    }

    async fieldChanged() {
        const newSettings = JSON.parse(JSON.stringify(this.state.gameSettings));
        Object.keys(newSettings).forEach(key => {
            newSettings[key] = document.getElementById(key).checked
        });

        await new Promise(resolve => this.setState({gameSettings: newSettings}, resolve));
        await this.buildProtectedRegions();
        await this.rewriteProtectedAreas();
    }

    render() {
        return (
            <Container className="credits-page">
                <Row>
                    <Col>
                        <h1>HUD Editor</h1>
                        <p className="mt-3">
                            The hud is the top area of the screen that shows on every level, telling you things like
                            the goal, and your key count. 
                        </p>

                        <h3>Settings</h3>
                        <Container>
                            <Row>
                                <Col><Form.Check type="checkbox" id="showGameTitle" label="Show game title" defaultChecked={this.state.gameSettings.showGameTitle} onChange={() => this.fieldChanged()} /></Col>
                                <Col><Form.Check type="checkbox" id="showGoal" label="Show goal" defaultChecked={this.state.gameSettings.showGoal} onChange={() => this.fieldChanged()} /></Col>
                            </Row>
                            <Row>
                                <Col><Form.Check type="checkbox" id="showLevelEnabled" label="Show current level" defaultChecked={this.state.gameSettings.showLevelEnabled} onChange={() => this.fieldChanged()} /></Col>
                                <Col><Form.Check type="checkbox" id="showKeyCount" label="Show key count" defaultChecked={this.state.gameSettings.showKeyCount} onChange={() => this.fieldChanged()} /></Col>
                            </Row>
                        </Container>
                        
                        <div className="mt-3">
                            <NamEditor 
                                ref={this.namEditor}
                                fieldName="hudData"
                                protectedRegions={this.state.protectedRegions}
                                onSave={() => this.doSave()}
                            />
                        </div>
                        <EditorFaq>
                            <Accordion.Item eventKey="hud-1">
                                <Accordion.Header>Why can't I edit past the first few lines?</Accordion.Header>
                                <Accordion.Body>
                                    <p>The way the game is written, the HUD only uses the first 6 rows of tiles. This isn't configurable, so you cannot go past that point. </p>
                                    <p>If you'd like to do this, the game's source is available on the Homepage, so you can change this yourself!</p>
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="hud-2">
                                <Accordion.Header>I unchecked a setting; why is it still there?</Accordion.Header>
                                <Accordion.Body>
                                    <p>
                                        Any changes made by turning on settings will update the graphics in the hud. The previous graphics aren't saved (this is
                                        rather challenging) so when you turn the setting back off, we leave the graphics as they were.
                                    </p>
                                    <p>
                                        When a setting is off, the text it controls can be easily replaced with whatever you like. (Including blank space.)
                                    </p>
                                </Accordion.Body>
                            </Accordion.Item>

                        </EditorFaq>
                    </Col>
                </Row>
            </Container>
        );
    }
}